/* eslint-disable react-hooks/exhaustive-deps */
// @ts-ignore - CSS imports are handled by the project build tooling, not TS declarations.
import './SimpleChatBox.css';
import React, { useState, useRef, useEffect } from 'react';
import { Avatar, Typography, IconButton, TextField, Popover } from '@mui/material';
import {
  Close as CloseIcon,
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  EmojiEmotions as EmojiEmotionsIcon,
  Search as SearchIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
} from '@mui/icons-material';
import { DeleteOutlined } from '@ant-design/icons';
import websocketService, { ChatMessage, CamChatFile } from 'services/websocketService';
import { documentsApi } from 'services/camMirrorService';
import { triggerDownload } from 'utils/triggerDownload';
import counselorServices from 'services/counselorServices';
import { groupMessagesByDate, COMMON_EMOJIS } from 'utils/dateUtils';
import DateSeparator from './DateSeparator';
import http from 'services/http';
import { Snackbar, Alert } from '@mui/material';
import { useUnreadCountsContext } from 'components/UnreadCountsContext';
import notificationServices from 'services/notificationServices';
import { useAppSelector } from 'app/hooks';
import { checkEnableParseJSON } from './helper/helper';
import { getUserId } from 'utils/sessionStorage';
import { STRIPE_PRICE_NAME } from 'constants/common';
import { SmsDisabledBanner } from './chat/SmsDisabledBanner';
import { ChatFilesTab } from './chat/ChatFilesTab';
import { canUseSms } from 'utils/smsPlanGate';
// import { useHistory } from 'react-router';
// import PaymentServices from 'services/payment';
// import { routers } from 'router/router';

// Track joined rooms at module level to persist across component remounts
const joinedRooms = new Set<string>();

/** Extra space above the viewport bottom so this panel does not cover Intercom’s launcher (default ~48px circle + ~20px inset + badge). */
const INTERCOM_LAUNCHER_BOTTOM_RESERVE_PX = 80;

interface SimpleChatBoxProps {
  participant: {
    id: number;
    name: string;
    email?: string;
    type: 'student' | 'counselor' | 'parent';
    avatar?: string;
    phoneNumber?: string;
    isVerifiedPhoneNumber?: boolean;
    studentInfo?: {
      id: number;
      name: string;
      yearFinishHighSchool?: number;
      phoneNumber?: string;
    };
    conversationId?: number;
    studentId?: number;
    counselorId?: number;
    parentId?: number;
    sessionId?: string;
    subscriptionPlan?: string;
  };
  onClose: () => void;
  index?: number; // Index for positioning (0, 1, 2)
  onMessageRead?: () => void; // Callback when messages are read
  markMessageAsRead?: (messageId: number) => void; // Function to mark message as read
  // Optional: when opening from search, scroll to this message id once
  initialScrollMessageId?: number;
}

interface FilePreview {
  file: File;
  preview: string;
  name: string;
  size: number;
  type: string;
}

const SimpleChatBox: React.FC<SimpleChatBoxProps> = ({
  participant,
  onClose,
  index = 0,
  onMessageRead,
  markMessageAsRead,
  initialScrollMessageId,
}) => {
  const [message, setMessage] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [emojiAnchorEl, setEmojiAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [selectedFile, setSelectedFile] = useState<FilePreview | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'error',
  });
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const textFieldRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const userId = Number(getUserId());
  const userRole = (sessionStorage.getItem('userRole') || '').toLowerCase() as
    | 'student'
    | 'counselor'
    | 'parent';
  const [conversationSideId, setConversationSideId] = useState<any>(null);
  const conversationId = `session_${participant.sessionId}`;
  const [isSmsNotificationEnabled, setIsSmsNotificationEnabled] = useState(false);
  // HEADER-DOCS-011: false until the recipient's notification settings are fetched,
  // so the SMS banner does not flash "SMS is off" before the real value arrives.
  const [isSmsSettingLoaded, setIsSmsSettingLoaded] = useState(false);
  // const [isEmailNotificationEnabled, setIsEmailNotificationEnabled] = useState(false);
  const basicInformationStore = useAppSelector((store) => store.basicInformation.myBasicInformation);
  const stripeSubscriptionInfo = useAppSelector((store) => store.basicInformation.stripeSubscriptionInfo);
  const stripeSubscriptionInfoLoaded = useAppSelector(
    (store) => store.basicInformation.stripeSubscriptionInfoLoaded
  );
  // const history = useHistory();

  // Subscription gate: UX overlay + guard in handleSend to avoid bypass via devtools. Backend must also reject send when plan not allowed.
  // Parent/counselor: block when participant has no plan (""/undefined) or has GradMapStarter (must upgrade to chat).
  const showUpgradeOverlay = (() => {
    if (userRole === 'student') {
      return Boolean(stripeSubscriptionInfo?.subcription_plan === STRIPE_PRICE_NAME.GradMapStarter);
    }
    if (userRole === 'parent' || userRole === 'counselor') {
      const plan = participant.subscriptionPlan;
      return !plan || plan === STRIPE_PRICE_NAME.GradMapStarter;
    }
    return false;
  })();

  // HEADER-DOCS-024: SMS sync gate, separate from the chat overlay/room gate above.
  // Chat text stays allowed on every plan; only SMS needs a paid STUDENT plan
  // (Silver Plus / Gold). Student: wait until Stripe info is loaded so the banner
  // does not flash; parent/counselor: use the student's plan on the participant.
  const smsBlockedByPlan = (() => {
    if (userRole === 'student') {
      return stripeSubscriptionInfoLoaded && !canUseSms(stripeSubscriptionInfo?.subcription_plan);
    }
    if (userRole === 'parent' || userRole === 'counselor') {
      return !canUseSms(participant.subscriptionPlan);
    }
    return false;
  })();

  /**
   * Student: chỉ join WS sau khi Redux đã có kết quả getStripeSubscriptionInfo.
   * Nếu join trước khi load, `subcription_plan` là undefined → không overlay → join nhầm; sau khi load thành Starter
   * thì `joinedRooms` đã có id nên không join lại nhưng vẫn coi như đã vào room.
   */
  // const canJoinWebsocketRoom =
  //   userRole === 'student'
  //     ? stripeSubscriptionInfoLoaded && !showUpgradeOverlay
  //     : !showUpgradeOverlay;

  // HEADER-DOCS-026: Chat | Files tabs. Files is shown only on a student<->counselor
  // thread, to the student or the counselor (parents never see it, V1 parent role).
  const [activeTab, setActiveTab] = useState<'chat' | 'files'>('chat');
  const canSeeFilesTab =
    (userRole === 'student' && participant.type === 'counselor') ||
    (userRole === 'counselor' && participant.type === 'student');
  const filesStudentId =
    userRole === 'student'
      ? userId
      : Number(participant.studentId || participant.studentInfo?.id || participant.id || 0);
  const isFilesTab = canSeeFilesTab && activeTab === 'files';

  useEffect(() => {
    // A different thread always opens on the Chat tab.
    setActiveTab('chat');
  }, [conversationId]);

  // Search states
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchCount, setSearchCount] = useState(0);
  const [currentSearchIndex, setCurrentSearchIndex] = useState(-1);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Helper function to normalize ID comparison (handles string/number type mismatches)
  const isMessageFromUser = (messageFromId: number | string | undefined): boolean => {
    if (messageFromId === undefined || userId === undefined || isNaN(Number(userId))) {
      return false;
    }
    return Number(messageFromId) === Number(userId);
  };

  // Helper function to fetch and parse notification settings
  const getNotificationSettings = async (participantId: number) => {
    const res: any = await notificationServices.getNotification(participantId);
    if (res) {
      const settings = checkEnableParseJSON(res.email_notification_array, []);
      if (settings) {
        const smsSetting = settings.find(
          (item: any) =>
            item.noti_type === 'sms' && item.type === 'receive' && item.source === 'in-app' && item.is_enabled === true
        );
        const emailSetting = settings.find(
          (item: any) =>
            item.noti_type === 'email' && item.type === 'receive' && item.source === 'in-app' && item.is_enabled === true
        );
        return {
          isSmsEnabled: !!smsSetting,
          isEmailEnabled: !!emailSetting,
        };
      }
    }
    return {
      isSmsEnabled: false,
      isEmailEnabled: false,
    };
  };

  useEffect(() => {
    const getSmsNotificationEnabled = async () => {
      setIsSmsSettingLoaded(false);
      try {
        const settings = await getNotificationSettings(participant.id);
        setIsSmsNotificationEnabled(settings.isSmsEnabled);
        // setIsEmailNotificationEnabled(settings.isEmailEnabled);
        setIsSmsSettingLoaded(true);
      } catch {
        // Keep the banner hidden when the settings request fails (unknown state).
      }
    };
    getSmsNotificationEnabled();
  }, [participant.id]);

  // Hook for managing unread counts from context
  const { decreaseUnreadCount } = useUnreadCountsContext();
  // Get contact information from basicInformation slice
  // const contactDetail = useAppSelector((store) => store.basicInformation.myContactDetail);
  // Get initials for avatar
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((word) => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  useEffect(() => {
    getConversationInfo();
  }, [conversationId]);

  const getConversationInfo = async () => {
    const res: any = await notificationServices.getConversation(conversationId);
    if (res) {
      setConversationSideId(res.conversation_sid);
    } else {
      setConversationSideId(null);
    }
  };

  const handleSend = async () => {
    // Enforce subscription at send time so removing overlay via F12 doesn't allow sending (backend must also validate)
    // if (showUpgradeOverlay) {
    //   setSnackbar({
    //     open: true,
    //     message: 'Upgrade your plan to use this chat function.',
    //     severity: 'error',
    //   });
    //   return;
    // }
    if ((!message.trim() && !selectedFile) || isSending || isUploading) return;

    const messageContent = message.trim();
    setMessage('');
    setIsSending(true);

    try {
      // Send typing stop event when sending message
      websocketService.sendTyping({
        conversation_id: conversationId,
        user_id: userId,
        user_type: userRole,
        action: 'stop',
      });

      // Parse session room format: session_633-542
      // Or get from participant
      let studentId = participant.studentId || 0;
      let counselorId = participant.counselorId || 0;
      let parentId = participant.parentId || 0;
      let phoneNumber = participant.phoneNumber || '';
      let isVerifiedPhoneNumber = participant.isVerifiedPhoneNumber || false;
      // If participant is counselor, counselorId is participant.id
      if (participant.type === 'counselor') {
        counselorId = participant.id;
      }
      // If participant is student, studentId might be participant.id
      if (participant.type === 'student') {
        studentId = participant.id || participant.studentInfo?.id || 0;
      }

      // For file upload, ensure we have valid IDs
      // HEADER-DOCS-026 follow-up: a student is always the student of their own thread.
      // participant.studentId on a counselor row can hold another id (it sent the
      // counselor id as student_id), so never take it from the participant here.
      if (userRole === 'student') {
        studentId = userId;
      }
      // If current user is counselor, use userId as counselorId
      if (userRole === 'counselor' && counselorId === 0) {
        counselorId = userId;
      }

      if (userRole === 'parent' && parentId === 0) {
        parentId = userId;
      }

      // Determine to_id and from_id based on userRole and participant type
      let toId = 0;
      let fromId = 0;

      if (userRole === 'student') {
        // Student is sending
        if (participant.type === 'counselor') {
          toId = counselorId;
          fromId = userId;
        } else if (participant.type === 'parent') {
          toId = parentId;
          fromId = userId;
        }
      } else if (userRole === 'counselor') {
        // Counselor is sending
        if (participant.type === 'student') {
          toId = studentId;
          fromId = userId;
        } else if (participant.type === 'parent') {
          toId = parentId;
          fromId = userId;
        }
      } else if (userRole === 'parent') {
        // Parent is sending
        if (participant.type === 'student') {
          toId = studentId;
          fromId = userId;
        } else if (participant.type === 'counselor') {
          toId = counselorId;
          fromId = userId;
        }
      }

      // If there's a file to upload
      if (selectedFile) {
        setIsUploading(true);

        try {
          // Upload file first
          const formData = new FormData();
          formData.append('file', selectedFile.file);
          formData.append('student_id', studentId.toString());
          formData.append('counselor_id', counselorId.toString());
          formData.append('user_id', userId.toString());
          formData.append('conversation_id', conversationId);

          const uploadResponse: any = await http.post('/api/chat/attachments/upload', formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });

          if (uploadResponse?.status === 'success') {
            // Clear selected file
            removeSelectedFile();

            // Nếu có text message kèm theo file, gửi text message riêng
            if (messageContent.trim()) {
              // Gửi text message riêng
              await websocketService.sendMessage({
                conversation_id: conversationId,
                student_id:
                  userRole === 'student' ? studentId : userRole === 'parent' ? parentId : studentId,
                counselor_id:
                  userRole === 'counselor'
                    ? counselorId
                    : userRole === 'parent'
                    ? counselorId
                    : counselorId,
                content: messageContent,
                creator: userRole,
                status: 'sent',
                to_id: toId,
                from_id: fromId,
              });
            }

            // Reset state và thoát
            // File message sẽ được hiển thị tự động qua WebSocket onNewMessage event
            setIsUploading(false);
            setIsSending(false);
            return;
          } else {
            throw new Error('Upload failed');
          }
        } catch (uploadError) {
          console.error('Failed to upload file:', uploadError);
          setSnackbar({
            open: true,
            message: 'Failed to upload file. Please try again.',
            severity: 'error',
          });
          setIsUploading(false);
          setIsSending(false);
          setMessage(messageContent);
          return;
        }
      }

      // Send message via WebSocket (chỉ khi không có file upload)
      await websocketService.sendMessage({
        conversation_id: conversationId,
        student_id:
          userRole === 'student' ? studentId : userRole === 'parent' ? parentId : studentId,
        counselor_id:
          userRole === 'counselor'
            ? counselorId
            : userRole === 'parent'
            ? counselorId
            : counselorId,
        content: messageContent,
        creator: userRole,
        status: 'sent',
        to_id: toId,
        from_id: fromId,
      });

      // Fetch latest notification settings before sending notifications
      // This ensures we always use the most up-to-date values even if another user
      // enabled notifications while the chatbox was open
      const notificationSettings = await getNotificationSettings(participant.id);
      const currentSmsEnabled = notificationSettings.isSmsEnabled;
      // const currentEmailEnabled = notificationSettings.isEmailEnabled;

      // Update state to reflect latest values
      setIsSmsNotificationEnabled(currentSmsEnabled);
      //setIsEmailNotificationEnabled(currentEmailEnabled);

      // Check contact conditions before sending SMS
      if (phoneNumber && isVerifiedPhoneNumber && conversationSideId && currentSmsEnabled && !smsBlockedByPlan) {
        await notificationServices.sendSMSToConversation(conversationSideId, messageContent);
      }
      // Check contact conditions before sending Email
      if (participant.email) {
        await notificationServices.sendEmail({
          user_id: userId,
          recipients: [{ email: participant.email, name: participant.name }],
          subject: 'New message from ' + basicInformationStore?.first_name + ' ' + basicInformationStore?.last_name,
          html: `<p>${messageContent}</p>`,
          senderName: basicInformationStore?.first_name + ' ' + basicInformationStore?.last_name,
          senderEmail: basicInformationStore?.email,
        });
      }
      // The message will be added to the list via the onNewMessage event listener
    } catch (error) {
      console.error('Failed to send message:', error);
      // Restore the message content if sending failed
      setMessage(messageContent);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Handle typing events
  const handleMessageChange = (value: string) => {
    setMessage(value);

    if (value.length > 0) {
      // Send typing start event
      websocketService.sendTyping({
        conversation_id: conversationId,
        user_id: userId,
        user_type: userRole,
        action: 'start',
      });
    } else {
      // Send typing stop event
      websocketService.sendTyping({
        conversation_id: conversationId,
        user_id: userId,
        user_type: userRole,
        action: 'stop',
      });
    }
  };

  // Mark messages as read when user focuses on input field
  const handleInputFocus = () => {
    if (messages.length > 0) {
      // Mark all unread messages as read when user focuses on input
      // Chỉ mark những tin nhắn từ người khác gửi cho mình (không phải tin nhắn mình gửi)
      const unreadMessages = messages.filter(
        (msg) => msg.status !== 'read' && !isMessageFromUser(msg.from_id)
      );

      if (unreadMessages.length > 0) {
        // Gọi onMessageRead callback để thông báo đã đánh dấu tin nhắn đã đọc
        if (onMessageRead) {
          onMessageRead();
        }

        // Giảm unread count ngay lập tức khi mark messages as read
        decreaseUnreadCount(conversationId, unreadMessages.length);

        unreadMessages.forEach(async (msg) => {
          if (msg.message_id) {
            try {
              await markMessageAsReadInternal(msg.message_id);
              // Cập nhật state ngay lập tức để UI phản hồi nhanh
              updateMessageStatus(msg.message_id, 'read');
            } catch (error) {
              console.error('Failed to mark message as read:', error);
            }
          }
        });
      }
    }
  };

  // Mark messages as read when user scrolls to bottom
  const handleScroll = () => {
    if (messagesContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10; // 10px threshold

      if (isAtBottom && messages.length > 0) {
        // Mark all unread messages as read when user scrolls to bottom
        // Chỉ mark những tin nhắn từ người khác gửi cho mình (không phải tin nhắn mình gửi)
        const unreadMessages = messages.filter(
          (msg) => msg.status !== 'read' && !isMessageFromUser(msg.from_id)
        );

        if (unreadMessages.length > 0) {
          // Gọi onMessageRead callback để thông báo đã đánh dấu tin nhắn đã đọc
          if (onMessageRead) {
            onMessageRead();
          }

          // Giảm unread count ngay lập tức khi mark messages as read
          decreaseUnreadCount(conversationId, unreadMessages.length);

          unreadMessages.forEach(async (msg) => {
            if (msg.message_id) {
              try {
                await markMessageAsReadInternal(msg.message_id);
                // Cập nhật state ngay lập tức để UI phản hồi nhanh
                updateMessageStatus(msg.message_id, 'read');
              } catch (error) {
                console.error('Failed to mark scrolled message as read:', error);
              }
            }
          });
        }
      }
    }
  };

  // Stop typing when user stops typing for 1 second
  useEffect(() => {
    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Only set timeout if message is not empty
    if (message.length === 0) {
      return;
    }

    // Set new timeout to send stop typing after 1 second of no changes
    typingTimeoutRef.current = setTimeout(() => {
      websocketService.sendTyping({
        conversation_id: conversationId,
        user_id: userId,
        user_type: userRole,
        action: 'stop',
      });
    }, 1000);

    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [message, conversationId, userId, userRole]);
  // Handle emoji picker
  const handleEmojiClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setEmojiAnchorEl(event.currentTarget);
    setShowEmojiPicker(true);
  };

  const handleEmojiClose = () => {
    setShowEmojiPicker(false);
    setEmojiAnchorEl(null);
  };

  const handleEmojiSelect = (emoji: string) => {
    setMessage((prev) => prev + emoji);
    handleEmojiClose();
    // Focus back to text field
    if (textFieldRef.current) {
      textFieldRef.current.focus();
    }
  };

  // Handle file selection
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setSnackbar({
          open: true,
          message: 'File size must be less than 10MB',
          severity: 'error',
        });
        return;
      }

      // Create preview for images
      let preview = '';
      if (file.type.startsWith('image/')) {
        preview = URL.createObjectURL(file);
      }

      setSelectedFile({
        file,
        preview,
        name: file.name,
        size: file.size,
        type: file.type,
      });
    }
  };

  // Remove selected file
  const removeSelectedFile = () => {
    if (selectedFile?.preview) {
      URL.revokeObjectURL(selectedFile.preview);
    }
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Format file size
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Calculate position based on index
  const getPositionStyle = () => {
    const baseRight = 20;
    const baseBottom = 20 + INTERCOM_LAUNCHER_BOTTOM_RESERVE_PX;
    const chatboxWidth = 380;
    const chatboxGap = 20;
    const chatboxHeight = 500;

    // Stack horizontally: 1st at right: 20px, 2nd at right: 420px, 3rd at right: 820px
    // If screen is too narrow, stack vertically
    const right = baseRight + (index % 3) * (chatboxWidth + chatboxGap);
    const bottom = baseBottom + Math.floor(index / 3) * (chatboxHeight + chatboxGap);

    return {
      right: `${right}px`,
      bottom: `${bottom}px`,
    };
  };
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [typingUser, setTypingUser] = useState<string>('');
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Store callback references for proper cleanup
  const typingCallbackRef = useRef<((data: any) => void) | null>(null);
  const messageCallbackRef = useRef<((message: ChatMessage) => void) | null>(null);
  const messageReadCallbackRef = useRef<((data: any) => void) | null>(null);

  // Track whether we've already scrolled to the initial message id
  const hasScrolledToInitialMessageRef = useRef(false);
  const previousInitialScrollIdRef = useRef<number | undefined>(undefined);

  const scrollToBottom = () => {
    setTimeout(() => {
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      }
    }, 100);
  };

  // Update message status in local state
  const updateMessageStatus = (messageId: number, status: string) => {
    setMessages((prev) =>
      prev.map((msg) => (msg.message_id === messageId ? { ...msg, status } : msg))
    );
  };

  // Internal function to mark message as read (use prop if provided, otherwise use websocketService)
  const markMessageAsReadInternal = async (messageId: number) => {
    if (markMessageAsRead) {
      await markMessageAsRead(messageId);
    } else {
      // Use websocketService directly if no prop provided
      websocketService.readMessage(conversationId, userId, messageId);
    }
  };

  const openAttachment = async (event: React.MouseEvent, attachmentId: number) => {
    event.preventDefault();
    try {
      const res: any = await http.get(`/api/chat/attachments/download/${attachmentId}`);
      const url = res?.data?.download_url;
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      // http interceptor already surfaces the error
    }
  };

  // HEADER-DOCS-025: files sent from CAM are Documents rows; ask for a fresh URL and download in place.
  const openCamFile = async (event: React.MouseEvent, file: CamChatFile) => {
    event.preventDefault();
    try {
      const { downloadUrl } = await documentsApi.getDownloadUrl(file.externalId);
      if (downloadUrl) triggerDownload(downloadUrl, file.displayName);
    } catch {
      // http interceptor already surfaces the error
    }
  };

  const loadInitialMessages = async () => {
    try {
      setIsLoading(true);
      const res: any = await counselorServices.getChatNotesMessage(
        `session_${participant.sessionId}`
      );

      // Sort messages by created_at timestamp (oldest first)
      const sortedMessages = res.sort(
        (a: ChatMessage, b: ChatMessage) =>
          new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime()
      );

      setMessages(sortedMessages);

      // Scroll to bottom after messages are loaded
      setTimeout(() => {
        scrollToBottom();
      }, 200);
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const initializeWebSocket = async () => {
      try {
        await websocketService.connect();
        const connected = websocketService.isSocketConnected();
        setIsConnected(connected);

        // Join room only when subscription allows chat (same gate as overlay; student waits for stripe info loaded)
        if (connected && !joinedRooms.has(conversationId)) {
          try {
            await websocketService.joinRoom({
              session_id: conversationId,
              user_id: userId,
              user_type: userRole,
            });
            joinedRooms.add(conversationId);
          } catch (error) {
            console.error('Failed to join room:', error);
          }
        }

        // Set up event listeners for incoming messages
        const messageCallback = (newMessage: ChatMessage) => {
          // Only handle messages for this specific conversation
          if (newMessage.conversation_id === conversationId) {
            // Check for duplicate messages before adding
            setMessages((prev) => {
              // Check if message already exists by message_id
              const messageExists = prev.some(
                (msg) =>
                  msg.message_id === newMessage.message_id &&
                  msg.content === newMessage.content &&
                  msg.from_id === newMessage.from_id
              );

              if (messageExists) {
                return prev;
              }

              // Add new message and sort by created_at
              const updatedMessages = [...prev, newMessage].sort(
                (a: ChatMessage, b: ChatMessage) =>
                  new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime()
              );

              return updatedMessages;
            });
          }

          // Note: Unread count refetch is handled by useUnreadCounts hook
          // which has a single WebSocket listener for all new messages
        };
        messageCallbackRef.current = messageCallback;
        websocketService.onNewMessage(messageCallback);

        // Set up event listener for typing indicator
        const typingCallback = (data: any) => {
          // Only show typing indicator if it's not from current user
          // Check conversation_id if available, otherwise just check user_id
          const isSameConversation =
            !data.session_id || `session_${data.session_id}` === conversationId;
          const isOtherUser = data.user_id !== userId;

          if (isOtherUser && isSameConversation) {
            setIsTyping(data.action === 'start');

            // Set typing user name based on participant type
            if (data.action === 'start') {
              setTypingUser(participant.name);

              // Clear existing timeout
              if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
              }

              // Auto-hide typing indicator after 3 seconds
              typingTimeoutRef.current = setTimeout(() => {
                setIsTyping(false);
                setTypingUser('');
              }, 3000);
            } else {
              setIsTyping(false);
              setTypingUser('');
            }
          }
        };
        typingCallbackRef.current = typingCallback;
        websocketService.onUserTyping(typingCallback);

        // Set up event listener for message read events
        const messageReadCallback = (data: any) => {
          // Only update if message belongs to current conversation
          if (data.conversation_id === conversationId) {
            setMessages((prev) => {
              let found = false;
              const updatedMessages = prev.map((msg) => {
                if (msg.message_id === data.message_id) {
                  found = true;
                  return { ...msg, status: 'read' };
                }
                return msg;
              });

              if (!found) {
                // Reload messages to get updated status from server
                setTimeout(() => {
                  loadInitialMessages();
                }, 100);
              }

              return updatedMessages;
            });
          }
        };
        messageReadCallbackRef.current = messageReadCallback;
        websocketService.onMessageRead(messageReadCallback);
      } catch (error) {
        console.error('Failed to initialize WebSocket:', error);
      }
    };
    initializeWebSocket();

    // Cleanup on unmount
    return () => {
      // Remove specific callbacks to prevent multiple listeners
      const socket = websocketService.getSocket();
      if (socket) {
        if (typingCallbackRef.current) {
          socket.off('chat:user_typing', typingCallbackRef.current);
          typingCallbackRef.current = null;
        }
        if (messageCallbackRef.current) {
          socket.off('chat:new_message', messageCallbackRef.current);
          messageCallbackRef.current = null;
        }
        if (messageReadCallbackRef.current) {
          socket.off('chat:message_read', messageReadCallbackRef.current);
          messageReadCallbackRef.current = null;
        }
      }
      // Clear typing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [conversationId, userId, userRole, showUpgradeOverlay]);

  // Nếu đã join room trước đó rồi mới biết là Starter (hoặc downgrade), leave và bỏ khỏi tập đã join
  useEffect(() => {
    if (userRole !== 'student' || !stripeSubscriptionInfoLoaded || !showUpgradeOverlay) return;
    if (!joinedRooms.has(conversationId)) return;

    let cancelled = false;
    (async () => {
      try {
        if (websocketService.isSocketConnected()) {
          await websocketService.leaveRoom({
            session_id: conversationId,
            user_id: userId,
            user_type: userRole,
          });
        }
      } catch (e) {
        console.error('Failed to leave room after subscription gate:', e);
      }
      if (!cancelled) {
        joinedRooms.delete(conversationId);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userRole, stripeSubscriptionInfoLoaded, showUpgradeOverlay, conversationId, userId]);

  // Cleanup file preview URL on unmount
  useEffect(() => {
    return () => {
      if (selectedFile?.preview) {
        URL.revokeObjectURL(selectedFile.preview);
      }
    };
  }, [selectedFile]);

  // Stop typing indicator when conversation changes
  useEffect(() => {
    setIsTyping(false);
    setTypingUser('');
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
  }, [conversationId]);

  // Load messages on component mount
  useEffect(() => {
    loadInitialMessages();
  }, [participant.sessionId]);

  // Mark messages as read when messages are loaded and user is viewing
  useEffect(() => {
    if (messages.length > 0 && isConnected) {
      // Mark all unread messages as read when messages are loaded
      // Chỉ mark những tin nhắn từ người khác gửi cho mình (không phải tin nhắn mình gửi)
      const unreadMessages = messages.filter(
        (msg) => msg.status !== 'read' && !isMessageFromUser(msg.from_id)
      );

      if (unreadMessages.length > 0) {
        // Delay a bit to ensure user is viewing the chat
        const timer = setTimeout(() => {
          // Gọi onMessageRead callback để thông báo đã đánh dấu tin nhắn đã đọc
          if (onMessageRead) {
            onMessageRead();
          }

          // Giảm unread count ngay lập tức khi mark messages as read
          decreaseUnreadCount(conversationId, unreadMessages.length);

          unreadMessages.forEach(async (msg) => {
            if (msg.message_id) {
              try {
                await markMessageAsReadInternal(msg.message_id);
                // Cập nhật state ngay lập tức để UI phản hồi nhanh
                updateMessageStatus(msg.message_id, 'read');
              } catch (error) {
                console.error('Failed to mark message as read on load:', error);
              }
            }
          });
        }, 500); // Wait 500ms after messages load

        return () => clearTimeout(timer);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length, isConnected, userId, conversationId]); // Only run when messages count or connection changes

  // Auto scroll to bottom when new messages arrive or typing indicator shows
  useEffect(() => {
    if (messages.length > 0 || isTyping) {
      scrollToBottom();
    }
  }, [messages, isTyping]);

  // Debug: Log messages when they change
  useEffect(() => {
    // Check for duplicates
    const messageIds = messages.map((msg) => msg.message_id);
    const uniqueIds = new Set(messageIds);
    if (messageIds.length !== uniqueIds.size) {
      // Duplicate message IDs detected
    }
  }, [messages]);

  // Search functionality
  const performSearch = async (query: string) => {
    if (!query.trim() || !conversationId) {
      setSearchResults([]);
      setSearchCount(0);
      setCurrentSearchIndex(-1);
      return;
    }

    try {
      const response: any = await http.post('/api/partner/search-chat-notes', {
        conversation_id: conversationId,
        search_query: query.trim(),
      });

      if (response && response.results) {
        setSearchResults(response.results);
        setSearchCount(response.count || response.results.length);
        // Default to oldest message (last index - củ nhất)
        setCurrentSearchIndex(response.results.length > 0 ? response.results.length - 1 : -1);
        
        // Scroll to the oldest message (last result - củ nhất)
        if (response.results.length > 0) {
          setTimeout(() => {
            scrollToMessage(response.results[response.results.length - 1]);
          }, 100);
        }
      } else {
        setSearchResults([]);
        setSearchCount(0);
        setCurrentSearchIndex(-1);
      }
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
      setSearchCount(0);
      setCurrentSearchIndex(-1);
    }
  };

  // Debounce search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchQuery.trim()) {
      searchTimeoutRef.current = setTimeout(() => {
        performSearch(searchQuery);
      }, 150); // 150ms debounce (HEADER-DOCS-011, spec §4.8/§14)
    } else {
      setSearchResults([]);
      setSearchCount(0);
      setCurrentSearchIndex(-1);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, conversationId]);

  // Scroll to message by message_id
  const scrollToMessage = (result: any) => {
    if (!result || !result.message_id) return;
    
    // Try multiple times in case DOM hasn't rendered yet
    const tryScroll = (attempts = 0) => {
      const messageElement = document.querySelector(
        `[data-message-id="${result.message_id}"]`
      ) as HTMLElement;
      
      if (messageElement && messagesContainerRef.current) {
        const container = messagesContainerRef.current;
        const messageTop = messageElement.offsetTop;
        const containerHeight = container.clientHeight;
        
        // Scroll to center the message in view
        container.scrollTo({
          top: messageTop - containerHeight / 2 + messageElement.offsetHeight / 2,
          behavior: 'smooth',
        });

        // Highlight the message temporarily
        messageElement.classList.add('search-highlight');
        setTimeout(() => {
          messageElement.classList.remove('search-highlight');
        }, 2000);
      } else if (attempts < 5) {
        // Retry after a short delay if element not found
        setTimeout(() => tryScroll(attempts + 1), 100);
      }
    };
    
    tryScroll();
  };

  // Reset scroll flag when a new target message id comes in
  useEffect(() => {
    if (!initialScrollMessageId) return;
    if (previousInitialScrollIdRef.current !== initialScrollMessageId) {
      previousInitialScrollIdRef.current = initialScrollMessageId;
      hasScrolledToInitialMessageRef.current = false;
    }
  }, [initialScrollMessageId]);

  // When opening from global search, scroll to the specific message once
  useEffect(() => {
    if (!initialScrollMessageId || hasScrolledToInitialMessageRef.current) return;
    if (messages.length === 0) return;

    const target = { message_id: initialScrollMessageId };
    scrollToMessage(target);
    hasScrolledToInitialMessageRef.current = true;
  }, [initialScrollMessageId, messages]);

  // Navigate to next/previous search result
  // ^ (up) = đi về phía cũ hơn (index tăng) - vì hiện message củ nhất trước
  // v (down) = đi về phía mới hơn (index giảm) - vì hiện message củ nhất trước
  const navigateSearchResult = (direction: 'up' | 'down') => {
    if (searchResults.length === 0) return;

    let newIndex = currentSearchIndex;
    if (direction === 'up') {
      // Đi về phía cũ hơn (index tăng)
      if (currentSearchIndex < searchResults.length - 1) {
        newIndex = currentSearchIndex + 1;
      }
    } else {
      // Đi về phía mới hơn (index giảm)
      if (currentSearchIndex > 0) {
        newIndex = currentSearchIndex - 1;
      }
    }

    if (newIndex !== currentSearchIndex) {
      setCurrentSearchIndex(newIndex);
      scrollToMessage(searchResults[newIndex]);
    }
  };

  // Handle search toggle
  const handleSearchToggle = () => {
    setIsSearchOpen(!isSearchOpen);
    if (!isSearchOpen) {
      // Focus input when opening
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    } else {
      // Clear search when closing
      setSearchQuery('');
      setSearchResults([]);
      setSearchCount(0);
      setCurrentSearchIndex(-1);
    }
  };

  // Upgrade link: student = own billing; parent/counselor = student's billing
  // const handleUpgradeClick = async () => {
  //   if (userRole === 'student') {
  //     const returnUrl = `${window.location.origin}/#${routers.settings.path}?active-tab=5`;
  //     const studentId = basicInformationStore?.student_id ?? basicInformationStore?.id;
  //     if (studentId) {
  //       try {
  //         const billUrl: any = await PaymentServices.getUserBillPortalUrl(returnUrl, Number(studentId));
  //         if (billUrl) window.location.href = billUrl;
  //       } catch {
  //         history.push(`${routers.settings.path}?active-tab=5`);
  //       }
  //     } else {
  //       history.push(`${routers.settings.path}?active-tab=5`);
  //     }
  //   } else {
  //     const studentId = participant.studentId ?? participant.id;
  //     if (studentId) {
  //       try {
  //         const returnUrl = `${window.location.origin}/#${routers.settings.path}?active-tab=5`;
  //         const billUrl: any = await PaymentServices.getUserBillPortalUrl(returnUrl, Number(studentId));
  //         if (billUrl) window.location.href = billUrl;
  //       } catch {
  //         history.push(`${routers.settings.path}?active-tab=5`);
  //       }
  //     } else {
  //       history.push(`${routers.settings.path}?active-tab=5`);
  //     }
  //   }
  // };

  return (
    <div className="simple-chatbox" style={getPositionStyle()}>
      {/* Header */}
      <div className="simple-chatbox-header">
        <div className="simple-chatbox-header-info">
          <Avatar className={`${participant.type}-avatar`} sx={{ width: 36, height: 36 }}>
            {participant.avatar ? (
              <img src={participant.avatar} alt={participant.name} />
            ) : (
              getInitials(participant.name)
            )}
          </Avatar>
          <div className="simple-chatbox-header-text">
            <Typography variant="subtitle2" className="simple-chatbox-name">
              {participant.name}
            </Typography>
            <Typography component="div" variant="caption" className="simple-chatbox-subtitle">
              {participant.phoneNumber
                ? `******${participant.phoneNumber.slice(-4)}`
                : 'No phone number'}
              {participant.isVerifiedPhoneNumber ? (
                <span className="verified-tag"> (Verified)</span>
              ) : (
                <span className="unverified-tag"> (Unverified)</span>
              )}
            </Typography>
            <Typography
              component="div"
              variant="caption"
              className={`sms-status ${isSmsNotificationEnabled ? 'on' : 'off'}`}
            >
              {isSmsNotificationEnabled ? 'SMS receive: enabled' : 'SMS receive: disabled'}
              {/* {<div>-</div>
              <div style={{ color: isEmailNotificationEnabled ? 'green' : 'red' }}>
                {isEmailNotificationEnabled ? 'Email receive: enabled' : 'Email receive: disabled'}
              </div>} */}
            </Typography>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '4px' }}>
          {/* HEADER-DOCS-026: in-thread search applies to messages only */}
          {!isFilesTab && (
            <IconButton size="small" onClick={handleSearchToggle} className="simple-chatbox-search-button">
              <SearchIcon fontSize="small" />
            </IconButton>
          )}
          <IconButton size="small" onClick={onClose} className="simple-chatbox-close-button">
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      </div>

      {/* Body: messages + input (with optional upgrade overlay) */}
      <div className="simple-chatbox-body">
      {/* HEADER-DOCS-026: Chat | Files tabs (student/counselor on a student-counselor thread only) */}
      {canSeeFilesTab && (
        <div className="simple-chatbox-tabs" role="tablist" aria-label="Chat sections">
          <button
            type="button"
            role="tab"
            id="chat-tab-chat"
            aria-selected={!isFilesTab}
            aria-controls="chat-panel-chat"
            className={`simple-chatbox-tab${!isFilesTab ? ' active' : ''}`}
            onClick={() => setActiveTab('chat')}
          >
            Chat
          </button>
          <button
            type="button"
            role="tab"
            id="chat-tab-files"
            aria-selected={isFilesTab}
            aria-controls="chat-panel-files"
            className={`simple-chatbox-tab${isFilesTab ? ' active' : ''}`}
            onClick={() => {
              // Leaving the message view closes (and clears) the in-thread search.
              if (isSearchOpen) handleSearchToggle();
              setActiveTab('files');
            }}
          >
            Files
          </button>
        </div>
      )}

      {/* Files panel. Mounted only while active so it refetches on every open. */}
      {isFilesTab && (
        <div className="simple-chatbox-files" role="tabpanel" id="chat-panel-files" aria-labelledby="chat-tab-files">
          <ChatFilesTab
            studentId={filesStudentId}
            conversationId={conversationId}
            currentUserId={userId}
            otherPartyName={(participant.name || '').split(' ')[0] || participant.name}
          />
        </div>
      )}

      {/* Chat panel. Kept mounted (only hidden) so scroll position and refs survive a tab switch. */}
      <div
        className="simple-chatbox-chat-panel"
        role={canSeeFilesTab ? 'tabpanel' : undefined}
        id="chat-panel-chat"
        aria-labelledby={canSeeFilesTab ? 'chat-tab-chat' : undefined}
        style={isFilesTab ? { display: 'none' } : undefined}
      >
      {/* Search Bar */}
      {isSearchOpen && (
        <div className="simple-chatbox-search-bar">
          <TextField
            inputRef={searchInputRef}
            size="small"
            placeholder="Search this conversation..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              // HEADER-DOCS-011: Esc closes (and clears) the in-thread search.
              if (e.key === 'Escape') {
                e.stopPropagation();
                handleSearchToggle();
              }
            }}
            className="simple-chatbox-search-input"
            fullWidth
            InputProps={{
              endAdornment: searchQuery.trim() && (
                <div className="simple-chatbox-search-controls">
                  <Typography variant="caption" className="simple-chatbox-search-count">
                    {searchCount > 0 ? `${currentSearchIndex + 1} / ${searchCount}` : '0 / 0'}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => navigateSearchResult('up')}
                    className="simple-chatbox-search-nav-button"
                    disabled={searchCount === 0 || currentSearchIndex === searchResults.length - 1}
                  >
                    <KeyboardArrowUpIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => navigateSearchResult('down')}
                    className="simple-chatbox-search-nav-button"
                    disabled={searchCount === 0 || currentSearchIndex === 0}
                  >
                    <KeyboardArrowDownIcon fontSize="small" />
                  </IconButton>
                </div>
              ),
            }}
          />
        </div>
      )}

      {/* Messages Area */}
      <div className="simple-chatbox-messages" ref={messagesContainerRef} onScroll={handleScroll}>
        {isLoading ? (
          <div className="loading-container">
            <Typography variant="body2" color="text.secondary">
              Loading messages...
            </Typography>
          </div>
        ) : (
          <div className="simple-chatbox-messages-list">
            {messages.length === 0 ? (
              <div className="simple-chatbox-empty">
                <Typography variant="body2" color="text.secondary">
                  No messages yet. Start a conversation!
                </Typography>
              </div>
            ) : (
              groupMessagesByDate(messages).map((item, index) => {
                if (item.type === 'date') {
                  return <DateSeparator key={`date-${index}`} date={item.date || ''} />;
                }

                const message = item.message;
                const isOwnMessage = isMessageFromUser(message.from_id);

                // Get sender name based on creator type
                const getSenderName = () => {
                  if (isOwnMessage) return 'You';
                  return (participant.name || '').split(' ')[0] || 'Them';
                };

                // Format timestamp
                const formatTime = (timestamp: string) => {
                  const date = new Date(timestamp);
                  const now = new Date();
                  const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

                  if (diffInHours < 24) {
                    return date.toLocaleTimeString('en-US', {
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true,
                    });
                  } else {
                    return date.toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true,
                    });
                  }
                };

                // Highlight search text inside a message.
                // HEADER-DOCS-011 / spec §4.8: plain case-insensitive substring match
                // via indexOf — never build a RegExp from user input.
                const renderHighlightedText = (text: string) => {
                  const query = searchQuery.trim();
                  if (!query) return text;

                  const lowerText = text.toLowerCase();
                  const lowerQuery = query.toLowerCase();
                  // Some Unicode chars change length when lower-cased; offsets would
                  // drift, so fall back to plain text in that rare case.
                  if (lowerText.length !== text.length || lowerQuery.length !== query.length) {
                    return text;
                  }

                  const parts: React.ReactNode[] = [];
                  let cursor = 0;
                  let hit = lowerText.indexOf(lowerQuery, cursor);
                  if (hit === -1) return text;

                  while (hit !== -1) {
                    if (hit > cursor) {
                      parts.push(<span key={`t-${cursor}`}>{text.slice(cursor, hit)}</span>);
                    }
                    parts.push(
                      <span key={`h-${hit}`} className="search-text-highlight">
                        {text.slice(hit, hit + query.length)}
                      </span>
                    );
                    cursor = hit + query.length;
                    hit = lowerText.indexOf(lowerQuery, cursor);
                  }
                  if (cursor < text.length) {
                    parts.push(<span key={`t-${cursor}`}>{text.slice(cursor)}</span>);
                  }
                  return <>{parts}</>;
                };

                // Get message status icon
                const getStatusIcon = (status: string) => {
                  if (status === 'read') {
                    return (
                      <span className="status-icon read" title="Read" aria-label="Read">
                        ✓✓
                      </span>
                    );
                  }
                  return (
                    <span className="status-icon sent" title="Unread" aria-label="Unread">
                      ✓
                    </span>
                  );
                };

                return (
                  <div
                    key={message.message_id || `message-${index}`}
                    data-message-id={message.message_id}
                    className={`simple-message-item ${
                      isOwnMessage ? 'own-message' : 'other-message'
                    }`}
                  >
                    <div className={`msg-avatar${isOwnMessage ? ' mine' : ''}`} aria-hidden="true">
                      {isOwnMessage
                        ? getInitials(
                            [basicInformationStore?.first_name, basicInformationStore?.last_name].filter(Boolean).join(' ') ||
                              'Me',
                          )
                        : getInitials(participant.name || '')}
                    </div>
                    <div className="simple-message-content">
                      <div className="simple-message-bubble">
                        {/* Display text content with optional search highlight */}
                        {message.content && (
                          <div className="simple-message-text">
                            {renderHighlightedText(message.content)}
                          </div>
                        )}

                        {/* Display attachments */}
                        {message.attachments && message.attachments.length > 0 && (
                          <div className="simple-message-attachments">
                            {message.attachments.map((attachment: any) => (
                              <div
                                key={attachment.attachment_id}
                                className="simple-attachment-item"
                              >
                                <a
                                  href={`#attachment-${attachment.attachment_id}`}
                                  onClick={(event) => openAttachment(event, attachment.attachment_id)}
                                  className="simple-attachment-link"
                                  title={attachment.original_name}
                                >
                                  📎 {attachment.original_name}
                                </a>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* HEADER-DOCS-025: files sent from CAM (empty for parents) */}
                        {message.cam_files && message.cam_files.length > 0 && (
                          <div className="simple-message-attachments">
                            {message.cam_files.map((file: CamChatFile) => (
                              <div key={file.externalId} className="simple-attachment-item">
                                <a
                                  href={`#document-${file.externalId}`}
                                  onClick={(event) => openCamFile(event, file)}
                                  className="simple-attachment-link"
                                  title={file.displayName}
                                >
                                  📎 {file.displayName}
                                </a>
                              </div>
                            ))}
                          </div>
                        )}

                      </div>
                      <div className="simple-message-meta">
                        <Typography variant="caption" className="simple-message-time">
                          {getSenderName()} · {formatTime(message.created_at || '')}
                        </Typography>
                        {isOwnMessage && getStatusIcon(message.status)}
                      </div>
                    </div>
                  </div>
                );
              })
            )}

            {/* Typing Indicator */}
            {isTyping && (
              <div className="simple-message-item simple-typing-indicator other-message">
                <div className="simple-message-content">
                  <div className="simple-message-sender-info">
                    <Typography variant="caption" className="simple-sender-label">
                      {typingUser} is typing...
                    </Typography>
                  </div>
                  <div className="simple-message-bubble simple-typing-bubble">
                    <div className="simple-typing-dots">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* File Preview */}
      {selectedFile && (
        <div className="simple-file-preview-container">
          <div className="simple-file-preview">
            {selectedFile.preview ? (
              <img src={selectedFile.preview} alt="Preview" className="simple-file-preview-image" />
            ) : (
              <div className="simple-file-preview-icon">📄</div>
            )}
            <div className="simple-file-preview-info">
              <Typography variant="body2" className="simple-file-name">
                {selectedFile.name}
              </Typography>
              <Typography variant="caption" className="simple-file-size">
                {formatFileSize(selectedFile.size)}
              </Typography>
            </div>
            <IconButton
              size="small"
              onClick={removeSelectedFile}
              className="simple-remove-file-button"
            >
              <DeleteOutlined />
            </IconButton>
          </div>
        </div>
      )}

      {/* HEADER-DOCS-011: SMS-disabled warning, above the composer. Inputs mirror
          the SMS send condition in handleSend (phone verified + SMS on + no plan gate). */}
      <SmsDisabledBanner
        currentUserRole={userRole}
        recipientUserId={participant.id}
        recipientEmail={participant.email}
        planBlocksSms={smsBlockedByPlan}
        recipientSmsEnabled={isSmsSettingLoaded ? isSmsNotificationEnabled : null}
        recipientHasVerifiedPhone={Boolean(participant.phoneNumber && participant.isVerifiedPhoneNumber)}
      />

      {/* Input Area */}
      <div className="simple-chatbox-input">
        <input
          ref={fileInputRef}
          type="file"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
          accept="*/*"
        />
        <IconButton
          size="small"
          className="simple-chatbox-input-button"
          onClick={() => fileInputRef.current?.click()}
          disabled={!isConnected || isSending || isUploading}
        >
          <AttachFileIcon fontSize="small" />
        </IconButton>
        <TextField
          inputRef={textFieldRef}
          multiline
          maxRows={3}
          placeholder={isConnected ? 'Type a message...' : 'Connecting...'}
          value={message}
          onChange={(e) => handleMessageChange(e.target.value)}
          onKeyPress={handleKeyPress}
          onFocus={handleInputFocus}
          className="simple-chatbox-textfield"
          size="small"
          disabled={!isConnected || isSending || isUploading}
        />
        <IconButton
          size="small"
          className="simple-chatbox-input-button"
          onClick={handleEmojiClick}
          disabled={!isConnected || isSending || isUploading}
        >
          <EmojiEmotionsIcon fontSize="small" />
        </IconButton>
        <Popover
          open={showEmojiPicker && Boolean(emojiAnchorEl)}
          anchorEl={emojiAnchorEl}
          onClose={handleEmojiClose}
          disablePortal={false}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          PaperProps={{
            className: 'simple-emoji-picker-popover',
            sx: {
              padding: 0,
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
              overflow: 'hidden',
              zIndex: 1600,
            },
          }}
        >
          <div className="simple-emoji-picker-container">
            <div className="simple-emoji-picker-title">Choose an emoji</div>
            <div className="simple-emoji-picker">
              <div className="simple-emoji-grid">
                {COMMON_EMOJIS.map((emoji, index) => (
                  <button
                    key={index}
                    className="simple-emoji-button"
                    onClick={() => handleEmojiSelect(emoji)}
                    type="button"
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </Popover>
        <IconButton
          size="small"
          onClick={handleSend}
          disabled={(!message.trim() && !selectedFile) || !isConnected || isSending || isUploading}
          className="simple-chatbox-send-button"
          color="primary"
        >
          <SendIcon fontSize="small" />
        </IconButton>
      </div>
      </div>
      {/* /HEADER-DOCS-026 chat panel */}

      {/* Upgrade plan overlay: parent sees message + Upgrade link; counselor only message */}
      {/* {showUpgradeOverlay && (
        <div className="simple-chatbox-upgrade-overlay">
          <div className="simple-chatbox-upgrade-content">
            <Typography variant="body2" className="simple-chatbox-upgrade-text">
              Upgrade your plan to use this chat function.
            </Typography>
            {userRole === 'parent' && (
              <span
                className="simple-chatbox-upgrade-link"
                onClick={handleUpgradeClick}
                onKeyDown={(e) => e.key === 'Enter' && handleUpgradeClick()}
                role="button"
                tabIndex={0}
                title="Upgrade Subscription Plan"
              >
                Upgrade
              </span>
            )}
          </div>
        </div>
      )} */}
      </div>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default SimpleChatBox;
